// Scenario Wrapper Component
// Wraps any calculator component with scenario management functionality

import React from 'react';
import { SubscriptionManager } from './SubscriptionManager.js';
import { alertCheckoutFailure, startRecommendedCheckout } from './utils/recommendedCheckout.js';
import { useScenarioManagement } from './useScenarioManagement.js';
import { 
  SaveScenarioModal, 
  LoadScenarioModal, 
  ExportModal, 
  DisclaimerModal, 
  ScenarioToolbar 
} from './ScenarioUIComponents.js';

export const ScenarioWrapper = ({ 
  children, 
  calculatorType,
  getCurrentInputs,
  getCurrentResults,
  loadScenarioInputs,
  hasResults = false
}) => {
  const canManageScenarios = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO);

  const promptUpgradeForScenarios = () => {
    if (typeof window !== 'undefined') {
      const calculatorTier = calculatorType || 'advanced';
      startRecommendedCheckout({
        tier: calculatorTier === 'smsf' ? 'premium' : 'essential',
        billing: 'monthly',
        source: 'saved_scenario',
        calculatorTier,
        surface: 'scenario_save_restore',
      }).catch(alertCheckoutFailure);
    } else {
      alert('Scenario save/load is available for subscribers.');
    }
  };

  const {
    // State
    savedScenarios,
    showSaveModal,
    showLoadModal,
    showExportModal,
    showDisclaimer,
    scenarioName,
    currentScenario,

    // State setters
    setShowSaveModal,
    setShowLoadModal,
    setShowExportModal,
    setScenarioName,
    setCurrentScenario,

    // Handlers
    handleSaveScenario,
    handleLoadScenario,
    handleDeleteScenario,
    handleExportJSON,
    handleExportCSV,
    handleExportPDF,
    handleShowDisclaimer,
    handleAcceptDisclaimer,
    handleDeclineDisclaimer
  } = useScenarioManagement(calculatorType);

  // Save scenario with current inputs and results
  const onSave = () => {
    if (!canManageScenarios) {
      promptUpgradeForScenarios();
      return;
    }
    try {
      const inputs = getCurrentInputs();
      const results = getCurrentResults();
      
      if (!inputs) {
        alert('No inputs to save. Please configure the calculator first.');
        return;
      }

      // Update current scenario with latest results before saving
      const scenarioToSave = {
        ...currentScenario,
        inputs,
        results
      };
      setCurrentScenario(scenarioToSave);
      
      setShowSaveModal(true);
    } catch (error) {
      alert('Error preparing scenario. Please try again.');
    }
  };

  // Load scenario and apply inputs
  const onLoad = (scenario) => {
    if (!canManageScenarios) {
      promptUpgradeForScenarios();
      return;
    }
    handleLoadScenario(scenario, loadScenarioInputs);
  };

  // Export current scenario
  const onExport = () => {
    if (!canManageScenarios) {
      promptUpgradeForScenarios();
      return;
    }
    try {
      const inputs = getCurrentInputs();
      const results = getCurrentResults();
      
      if (!inputs) {
        alert('No data to export. Please run the calculator first.');
        return;
      }

      const scenarioToExport = currentScenario || {
        name: 'Current Session',
        calculatorType,
        createdAt: new Date().toISOString(),
        inputs,
        results
      };
      
      setCurrentScenario(scenarioToExport);
      setShowExportModal(true);
    } catch (error) {
      alert('Error preparing export. Please try again.');
    }
  };

  // Save scenario with name
  const onSaveWithName = (name) => {
    try {
      const inputs = getCurrentInputs();
      const results = getCurrentResults();
      
      const scenarioToSave = {
        ...currentScenario,
        inputs,
        results,
        name: name, // Set the name directly on the scenario object
        calculatorType: calculatorType, // Ensure calculatorType is always set
        createdAt: currentScenario?.createdAt || new Date().toISOString() // Ensure createdAt is always set
      };
      
      // Pass the scenario with the name directly
      handleSaveScenario(scenarioToSave, results);
    } catch (error) {
      alert('Error saving scenario. Please try again.');
    }
  };

  return (
    <>
      <ScenarioToolbar
        onSave={onSave}
        onLoad={() => {
          if (!canManageScenarios) {
            promptUpgradeForScenarios();
            return;
          }
          setShowLoadModal(true);
        }}
        onExport={onExport}
        onShowDisclaimer={handleShowDisclaimer}
        hasResults={hasResults}
        calculatorType={calculatorType}
        canManageScenarios={canManageScenarios}
      />
      
      {children}
      
      <SaveScenarioModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={onSaveWithName}
        scenarioName={scenarioName}
        setScenarioName={setScenarioName}
      />
      
      <LoadScenarioModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        scenarios={savedScenarios}
        onLoad={onLoad}
        onDelete={handleDeleteScenario}
      />
      
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        scenario={currentScenario}
        onExportJSON={handleExportJSON}
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
      />
      
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleAcceptDisclaimer}
        onDecline={handleDeclineDisclaimer}
      />
    </>
  );
};
